import { CheckCircle2, XCircle, Clock, Hourglass, MinusCircle, ArrowUpCircle } from 'lucide-react';
import { useApprovalProgress, ApprovalProgressStep } from '../../hooks/useApprovalProgress';
import { classNames } from '../../utils/helpers';

const META: Record<string, { icon: typeof CheckCircle2; color: string; ring: string }> = {
  Approved:  { icon: CheckCircle2, color: 'text-permus-success', ring: 'border-permus-success' },
  Rejected:  { icon: XCircle,      color: 'text-permus-danger',  ring: 'border-permus-danger' },
  Pending:   { icon: Clock,        color: 'text-amber-500',      ring: 'border-amber-400' },
  // Waiting its turn — an earlier step is still the one actually pending action.
  Queued:    { icon: Hourglass,    color: 'text-gray-400',       ring: 'border-gray-200' },
  Skipped:   { icon: MinusCircle,  color: 'text-gray-300',       ring: 'border-gray-200' },
  Delegated: { icon: ArrowUpCircle,color: 'text-sky-500',        ring: 'border-sky-400' },
  Escalated: { icon: ArrowUpCircle,color: 'text-orange-500',     ring: 'border-orange-400' },
  Cancelled: { icon: MinusCircle,  color: 'text-gray-300',       ring: 'border-gray-200' },
};

export default function ApprovalStepper({ ticketId }: { ticketId: string }) {
  const { steps, loading } = useApprovalProgress(ticketId);

  if (loading || steps.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
      <h4 className="text-sm font-semibold text-navy mb-3">Approval progress</h4>
      <ol className="relative">
        {steps.map((s: ApprovalProgressStep, i) => {
          const meta = META[s.status] ?? META.Pending;
          const Icon = meta.icon;
          const last = i === steps.length - 1;
          return (
            <li key={s.stepOrder} className="relative flex gap-3 pb-4">
              {!last && <span className="absolute left-[11px] top-6 bottom-0 w-px bg-gray-200" aria-hidden />}
              <span className={classNames('relative z-10 flex items-center justify-center w-6 h-6 rounded-full bg-white border-2', meta.ring)}>
                <Icon size={14} className={meta.color} />
              </span>
              <div className="flex-1 -mt-0.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-navy">{s.name}</span>
                  <span className={classNames('text-xs font-medium', meta.color)}>{s.status}</span>
                </div>
                <div className="text-xs text-gray-400">
                  {s.assigneeName ?? 'Unassigned'}
                  {s.actedAt && ` · ${new Date(s.actedAt).toLocaleDateString()}`}
                </div>
                {s.note && <p className="text-xs text-gray-500 mt-0.5 italic">“{s.note}”</p>}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
